import PropTypes from 'prop-types';
import React, { useRef, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '@ipc/ipc-components';
import {
  getMrdImpcIsLoading,
  getMrdImpcData,
  getMrdImpcColumns,
  getMrdAppTranslationsIsLoading,
  getMrdAppTranslationsData,
  getMrdAppTranslationsColumns,
  getPreservedImpc,
} from '../redux/selectors/MrdAppTranslationsSelectors';
import TableDefinitions from '../../../../common/TableDefinitions';
import ListTable from '../../../../common/list-table/components/ListTable';
import { tableLoadData } from '../../../../common/list-table/actions/ListTableActions';
import { MrdAppTranslationsStyles } from '../component/MrdAppTranslations.styles';
import { getUserRole } from '../../../../common/user/redux/selectors/UserSelectors';
import { editTranslationsRequestActionCreator, clearPreservedData } from '../redux/actions/MrdAppTranslationsActions';
import {
  blockRoutingAction,
  triggerConfirmationDialogAction,
  triggerRedirect,
} from '../../../../common/redux/actions/redirectActions';
import { formatMessage } from '../../../../../locales/locales';
import ConfirmationDialog from '../../../../common/list-table/helpers/confirmationDialog';
import { getShowConfirmationDialog } from '../../../../common/redux/selectors/redirectSelector';
import { editIpcColumns, editColumns } from './MrdAppTranslationsColumns';

function MrdAppTranslationsContainer(props) {
  const {
    isLoading,
    data,
    columns,
    mrdImpcColumns,
    mrdImpcIsLoading,
    mrdImpcData,
    tableLoad,
    userRole,
    editTranslations,
    setRouteBlocker,
    showConfirmationDialog,
    updateConfirmationDialog,
    confirmRedirect,
    preservedImpc,
    clearPreservedImpc,
  } = props;

  const [selectedMrdImpc, updateSelectedMrdImpc] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [visibleData, setVisibleData] = useState(data);
  const [editedRows, setEditedRows] = useState([]);
  const [isChangesValid, updateChangesValidity] = useState(true);
  const isUserIpcAdmin = userRole === 'IPC_ADMIN';
  const isUserOperAdmin = userRole === 'OPER_ADMIN';
  const lastLoadedImpc = useRef(selectedMrdImpc);
  useEffect(() => {
    tableLoad(TableDefinitions.MRD_IMPC_LIST_BY_COUNTRY.id);
  }, []);

  useEffect(() => {
    if (preservedImpc !== null) {
      updateSelectedMrdImpc(preservedImpc);
    }
  }, [preservedImpc, selectedMrdImpc]);

  useEffect(() => {
    if (mrdImpcData.length > 0 && selectedMrdImpc === '' && preservedImpc === null) {
      updateSelectedMrdImpc(mrdImpcData[0].impc);
    } else if (selectedMrdImpc !== '' && lastLoadedImpc.current !== selectedMrdImpc) {
      tableLoad(TableDefinitions.MRD_APP_TRANSLATIONS.id, { impcCode: selectedMrdImpc });
      lastLoadedImpc.current = selectedMrdImpc;
      clearPreservedImpc();
    }
  }, [mrdImpcData, selectedMrdImpc]);

  useEffect(() => {
    setRouteBlocker(editMode);
  }, [editMode]);

  useEffect(() => {
    const changes = visibleData.filter((row, index) => (data[index].originalValue !== row.originalValue)
      || (data[index].translatedValue !== null ? row.translatedValue !== data[index].translatedValue : (row.translatedValue !== '' && row.translatedValue !== null)));
    setEditedRows(changes);
    const validityArray = changes.filter((change) => change.originalValue === '');
    updateChangesValidity(validityArray.length === 0);
  }, [visibleData]);

  const getOnClickOfMrdImpcTable = (state, rowInfo) => {
    if (rowInfo) {
      return {
        style: {
          background: rowInfo.original.impc === selectedMrdImpc ? '#dbffdb' : null,
        },
        onClick: () => {
          if (!editMode) {
            updateSelectedMrdImpc(rowInfo.original.impc);
          }
        },
      };
    }
    return {};
  };

  const handleEditTranslations = async () => {
    const payload = editedRows.map((row) => ({
      impcCode: selectedMrdImpc,
      key: row.key,
      originalValue: row.originalValue,
      translatedValue: row.translatedValue,
    }));
    await editTranslations(payload);
  };

  const handleCloseEditMode = () => {
    setEditMode(false);
  };

  const handleRoutingWithUnsavedChanges = () => {
    setRouteBlocker(false);
    if (showConfirmationDialog) {
      updateConfirmationDialog(false);
      confirmRedirect(true);
    }
  };

  useEffect(() => { setVisibleData(data); }, [data]);
  return (
    <MrdAppTranslationsStyles>
      <div className="mrd-app-translations-container">
        <div className="mrd-app-translations-table-container">
          <div className="left-table">
            <ListTable
              listId={TableDefinitions.MRD_IMPC_LIST_BY_COUNTRY.id}
              columns={mrdImpcColumns}
              loading={mrdImpcIsLoading}
              data={mrdImpcData}
              getTrProps={getOnClickOfMrdImpcTable}
            />
          </div>
          {mrdImpcData.length > 0 && (
            <div className="right-table">
              {(isUserIpcAdmin || isUserOperAdmin) && (
                editMode ? (
                  <div className="mrd-app-translations-save-button-container">
                    <Button
                      onClick={handleEditTranslations}
                      className={`primary-button-${editedRows.length === 0 || !isChangesValid} mrd-app-translations-button`}
                      disabled={(editedRows.length === 0 || !isChangesValid)}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => {
                        setVisibleData(data);
                        handleCloseEditMode();
                      }}
                      className="primary-button-false mrd-app-translations-button"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="mrd-app-translations-edit-button-container">
                    <Button
                      onClick={() => { setEditMode(true); }}
                      className="primary-button-false mrd-app-translations-button"
                    >
                      Edit
                    </Button>
                  </div>
                )
              )}
              {!editMode && (
                <ListTable
                  listId={TableDefinitions.MRD_APP_TRANSLATIONS.id}
                  loading={isLoading}
                  columns={columns}
                  data={data}
                  isDownloadEnable
                />
              )}
              {editMode && isUserIpcAdmin && (
                <ListTable
                  listId={TableDefinitions.MRD_APP_TRANSLATIONS.id}
                  loading={isLoading}
                  columns={editMode ? editIpcColumns(visibleData, setVisibleData) : columns}
                  data={visibleData}
                />
              )}
              {editMode && isUserOperAdmin && (
                <ListTable
                  listId={TableDefinitions.MRD_APP_TRANSLATIONS.id}
                  loading={isLoading}
                  columns={editMode ? editColumns(visibleData, setVisibleData) : columns}
                  data={visibleData}
                />
              )}
            </div>
          )}
        </div>
        <ConfirmationDialog
          isOpen={showConfirmationDialog}
          submitButtonText="Yes"
          cancelButtonText="No"
          onClose={() => updateConfirmationDialog(false)}
          onSubmit={handleRoutingWithUnsavedChanges}
          confirmationTitle={formatMessage('en', 'translations.edit.unsaved.changes')}
        />
      </div>
    </MrdAppTranslationsStyles>
  );
}

function mapStateToProps(state) {
  return {
    data: getMrdAppTranslationsData(state),
    isLoading: getMrdAppTranslationsIsLoading(state),
    columns: getMrdAppTranslationsColumns(state),
    mrdImpcData: getMrdImpcData(state),
    mrdImpcColumns: getMrdImpcColumns(state),
    mrdImpcIsLoading: getMrdImpcIsLoading(state),
    userRole: getUserRole(state),
    showConfirmationDialog: getShowConfirmationDialog(state),
    preservedImpc: getPreservedImpc(state),
  };
}

MrdAppTranslationsContainer.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  mrdImpcData: PropTypes.arrayOf(PropTypes.object),
  mrdImpcColumns: PropTypes.arrayOf(PropTypes.object),
  mrdImpcIsLoading: PropTypes.bool,
  tableLoad: PropTypes.func,
  userRole: PropTypes.string,
  editTranslations: PropTypes.func,
  setRouteBlocker: PropTypes.func,
  showConfirmationDialog: PropTypes.bool,
  updateConfirmationDialog: PropTypes.func,
  confirmRedirect: PropTypes.func,
  preservedImpc: PropTypes.string,
  clearPreservedImpc: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  tableLoad: tableLoadData,
  editTranslations: editTranslationsRequestActionCreator,
  setRouteBlocker: blockRoutingAction,
  updateConfirmationDialog: triggerConfirmationDialogAction,
  confirmRedirect: triggerRedirect,
  clearPreservedImpc: clearPreservedData,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(MrdAppTranslationsContainer);
